/* eslint-disable max-lines */
import type { GridApi, GridOptions } from '@ag-grid-community/core';
import emptySvg from '@/assets/images/empty-state.svg';
import moment from 'moment-timezone';

// Type definitions for workstreams grid
export interface UserFilterData {
  id: string | number;
  label?: string;
  email?: string;
  teamId?: string | number;
  roleIds?: Array<string | number>;
  tags?: Array<string | number>;
  locationId?: string | number;
  teamName?: string;
  employeeId?: string | number;
  timezone?: string;  // Employment timezone
  endAt?: string | null;  // Employment end date (ISO 8601 format)
}

export interface UsersDataMap {
  [key: string]: {
    users?: Record<string, UserFilterData[]> | UserFilterData[];
    shiftTemplates?: Array<{ id: string; name: string; color?: string }>;
    shiftLocations?: Array<{ id: string; name: string; color?: string }>;
    tasks?: Array<{ id: string; name: string; label?: string }>;
    leaveTasks?: Array<{ id: string; name: string; label?: string }>;
  };
}

interface Shift {
  startTime?: string;
  [key: string]: unknown;
}

export interface ShiftResponseItem {
  user: {
    id: string | number;
    name?: string;
    email?: string;
    timezone?: string;
    teamName?: string;
    teamId?: string | number;
    employeeId?: string | number;
    endAt?: string | null;  // Employment end date (ISO 8601 format)
  };
  shifts?: Shift[];
  [key: string]: unknown;
}

export interface ApiResponse {
  data: ShiftResponseItem[];
  totalCount: number;
}

export interface TimeScheduleBody {
  userIds: string;
  startTime: string;
  endTime: string;
  shiftStartTime: string;
  shiftEndTime: string;
  taskIds: string;
  shiftLocationIds: string;
  taskStartTime: string;
  taskEndTime: string;
  shiftTemplateIds: string;
}

export interface FacetFilterValue {
  value?: string;
  boundaries?: string;
  matchMode?: 'any' | 'all';
}

export interface FacetFilters {
  [key: string]: FacetFilterValue;
}

// Helper: Check if user matches a specific filter
function checkUsersFilter(user: UserFilterData, facets: FacetFilters): boolean {
    if (!facets?.users?.value) return true;
    const ids = facets.users.value.split(',').map((v) => v.trim());
    return ids.includes(String(user.id));
}

function checkTeamsFilter(user: UserFilterData, facets: FacetFilters): boolean {
    if (!facets?.teams?.value) return true;
    const teamIds = facets.teams.value.split(',').map((v) => Number(v.trim()));
    return teamIds.includes(Number(user.teamId));
}

function checkBusinessRolesFilter(user: UserFilterData, facets: FacetFilters): boolean {
    if (!facets?.businessRoles?.value) return true;
    
    const selectedRoleIds = facets.businessRoles.value.split(',').map((v) => Number(v.trim()));
    const matchMode = facets.businessRoles.matchMode || 'any';
    
    if (!Array.isArray(user.roleIds) || user.roleIds.length === 0) {
        return false;
    }
    
    if (matchMode === 'all') {
        // AND logic: user must have ALL selected roles
        return selectedRoleIds.every((roleId) =>
            user?.roleIds?.some((userRoleId) => Number(userRoleId) === roleId)
        );
    } else {
        // OR logic: user must have ANY of the selected roles (default)
        return user?.roleIds?.some((roleId) =>
            selectedRoleIds.includes(Number(roleId))
        );
    }
}
 
function checkTagsFilter(user: UserFilterData, facets: FacetFilters): boolean {
    if (!facets?.tags?.value) return true;
    
    const selectedTags = facets.tags.value.split(',').map((v) => v.trim());
    const matchMode = facets.tags.matchMode || 'any';
    
    if (!Array.isArray(user.tags) || user.tags.length === 0) {
        return false;
    }
    
    if (matchMode === 'all') {
        // AND logic: user must have ALL selected tags
        return selectedTags.every((tag) =>
            user?.tags?.some((userTag) => String(userTag) === tag)
        );
    } else {
        // OR logic: user must have ANY of the selected tags (default)
        return user?.tags?.some((tag) =>
            selectedTags.includes(String(tag))
        );
    }
}

function checkLocationsFilter(user: UserFilterData, facets: FacetFilters): boolean {
    if (!facets?.locations?.value) return true;
    const locationIds = facets.locations.value.split(',').map((v: string) => v.trim());
    return locationIds.includes(String(user.locationId));
}

// Main function: Get user IDs from facets
export function getUsersIdsFromFacets(
    facets: FacetFilters,
    usersData: UsersDataMap | null,
    activeTable: string
): string {
    if (!usersData) return '';
  
    const users = usersData?.[activeTable]?.users
        ? Object.values(usersData[activeTable].users).flat()
        : [];

    if (!users || users.length === 0) return '';

    const userIds = users
        .filter((user) => {
            return (
                checkUsersFilter(user, facets) &&
        checkTeamsFilter(user, facets) &&
        checkBusinessRolesFilter(user, facets) &&
        checkTagsFilter(user, facets) &&
        checkLocationsFilter(user, facets)
            );
        })
        .map((u) => String(u.id));

    // If no filters applied → return ALL users
    const anyFilterApplied = Object.values(facets).some((f) => f?.value);
    if (!anyFilterApplied) {
        return users.map((u) => String(u.id)).join(',');
    }

    return userIds.join(',');
}

// Helper: Replace date in ISO string
function replaceDate(baseDate: string, timeSource: string): string {
    if (!baseDate || !timeSource) return timeSource || '';
    try {
        const base = new Date(baseDate);
        const time = new Date(timeSource);
        const result = new Date(Date.UTC(
            base.getUTCFullYear(),
            base.getUTCMonth(),
            base.getUTCDate(),
            time.getUTCHours(),
            time.getUTCMinutes(),
            time.getUTCSeconds(),
            time.getUTCMilliseconds()
        ));
        return result.toISOString();
    } catch {
        return timeSource;
    }
}

export interface GenerateBodyParams {
  facets: FacetFilters;
  usersData: UsersDataMap | null;
  activeTable: string;
  defaultDates: { start: string; end: string };
}

// Generate body for time schedule API request
export function generateBodyForTimeSchedule(params: GenerateBodyParams): TimeScheduleBody {
    const { facets, usersData, activeTable, defaultDates } = params;
    const getBoundary = (facetKey: string, index: number) =>
        facets?.[facetKey]?.boundaries?.split('_')[index] || '';
  
    const startTime = getBoundary('dateFacet', 0);
    const endTime = getBoundary('dateFacet', 1);
  
    const shiftStartTime = replaceDate(startTime, getBoundary('shiftStartFacet', 0));
    const shiftEndTime = replaceDate(endTime, getBoundary('shiftStartFacet', 1));
    const taskStartTime = replaceDate(startTime, getBoundary('tasksStartFacet', 0));
    const taskEndTime = replaceDate(endTime, getBoundary('tasksStartFacet', 1));
  
    const userIds = getUsersIdsFromFacets(facets, usersData, activeTable);
  
    return {
        userIds: userIds,
        startTime: startTime || defaultDates.start,
        endTime: endTime || defaultDates.end,
        shiftStartTime,
        shiftEndTime,
        taskIds: [ facets?.tasks?.value, facets?.leaveTasks?.value ].filter(Boolean).join(','),
        shiftLocationIds: facets?.shiftLocations?.value || '',
        taskStartTime,
        taskEndTime,
        shiftTemplateIds: facets?.shiftTemplates?.value || ''
    };
}

// Helper: Update single response item with user data
function updateResponseItemWithUserData(item: ShiftResponseItem, usersData: UserFilterData[]): void {
    const matchedUser = usersData.find((u) => u.id === item.user.id);
    if (!matchedUser) return;
    
    const {
        label = '',
        teamName = '',
        teamId = '',
        email = '',
        employeeId = '',
        endAt = null
    } = matchedUser;
    
    Object.assign(item.user, {
        name: label,
        teamName,
        teamId,
        email,
        employeeId,
        endAt
    });
}

// Add user names to API response
export function addUserNameInResponse(
    response: ShiftResponseItem[],
    users: UsersDataMap | null,
    activeTable: string
): ShiftResponseItem[] {
    if (!users) return response;
  
    const usersData = users?.[activeTable]?.users
        ? Object.values(users[activeTable].users).flat()
        : [];
  
    if (Array.isArray(usersData) && usersData.length > 0) {
        response.forEach((item) => updateResponseItemWithUserData(item, usersData));
    }
    return response;
}

/**
 * Helper: Build user employment data maps
 */
function buildUserEmploymentMaps(usersData: UserFilterData[]): {
    endAtMap: Map<string | number, string | null>;
    timezoneMap: Map<string | number, string>;
} {
    const endAtMap = new Map<string | number, string | null>();
    const timezoneMap = new Map<string | number, string>();
    
    usersData.forEach((user) => {
        if (user.endAt !== undefined) {
            endAtMap.set(user.id, user.endAt);
            timezoneMap.set(user.id, user.timezone || 'UTC');
        }
    });
    
    return { endAtMap, timezoneMap };
}

/**
 * Helper: Filter shifts for a user based on employment end date
 */
function filterUserShifts(
    item: ShiftResponseItem,
    employmentEndDate: string,
    userTimezone: string
): ShiftResponseItem {
    const filteredShifts = (item.shifts || []).filter((shift) => {
        if (!shift.startTime) {
            return false;
        }
        
        const shiftStartInEmploymentTZ = moment(shift.startTime).tz(userTimezone);
        const shiftStartDateOnly = shiftStartInEmploymentTZ.format('YYYY-MM-DD');
        
        return shiftStartDateOnly <= employmentEndDate;
    });

    // Always return the user item even if all shifts are filtered out
    // This maintains consistent behavior with mergeBackendResponse which handles
    // empty shift display based on whether shift/task filters are active
    return {
        ...item,
        shifts: filteredShifts
    };
}

/**
 * Filter shifts based on user employment end date
 * Ensures shifts beyond a user's employment end date are not displayed
 *
 * @param response - API response containing user shifts
 * @param users - User data map containing employment information
 * @param activeTable - Active table identifier
 * @returns Filtered response with shifts only up to employment end date
 */
export function filterShiftsByUserEndAt(
    response: ShiftResponseItem[],
    users: UsersDataMap | null,
    activeTable: string
): ShiftResponseItem[] {
    if (!users) {
        return response;
    }

    const usersData = users?.[activeTable]?.users
        ? Object.values(users[activeTable].users).flat()
        : [];

    if (!Array.isArray(usersData) || usersData.length === 0) {
        return response;
    }

    // Build employment data maps
    const { endAtMap, timezoneMap } = buildUserEmploymentMaps(usersData);

    // Filter shifts for each user based on their endAt
    return response.map((item) => {
        const userId = item.user.id;
        const userEndAt = endAtMap.get(userId);
        
        // If user has no endAt (permanent employee), return all shifts
        const userEmploymentTimezone = timezoneMap.get(userId) || 'UTC';
        if (!userEndAt) {
            return item;
        }

        // Get employment end date (calendar date only) in employment timezone
        const employmentEndMoment = moment.tz(userEndAt, userEmploymentTimezone);
        const employmentEndDateOnly = employmentEndMoment.format('YYYY-MM-DD');
        
        return filterUserShifts(item, employmentEndDateOnly, userEmploymentTimezone);
    });
}

// Grid helper: Get filtered row count
export function getFilteredRowCount(gridApi: GridApi | null): number {
    if (!gridApi) return 0;
    let filteredCount = 0;
    gridApi.forEachNodeAfterFilter((node) => {
        if (!node.group) {
            filteredCount++;
        }
    });
    return filteredCount;
}

// Grid helper: Update user column header with count
export function updateUserColumnHeader(gridApi: GridApi | null, groupBy: string): void {
    const filteredCount = getFilteredRowCount(gridApi);
    if (groupBy !== 'TEAMS')
    {
        const userColDef = gridApi?.getColumnDef('userDisplayName');
        if (userColDef) {
            userColDef.headerName = `Users (${filteredCount > 0 ? filteredCount : 0})`;
            gridApi?.refreshHeader();
        }
    }
    else
    {
        if (!gridApi) {
            return;
        }
        const autoGroupColDef = gridApi.getColumnDef('ag-Grid-AutoColumn');

        if (!autoGroupColDef) {
            return;
        }
        autoGroupColDef.headerName = `Teams and Users (${filteredCount > 0 ? filteredCount : 0})`;
        gridApi.refreshHeader();                        // ← update UI
    }
}

// Grid helper: Create grid options
export function createGridOptions(
    onFilterChangedCallback: () => void
): Partial<GridOptions> {
    return {
        domLayout: 'autoHeight',
        suppressLoadingOverlay: true, // Suppress AG Grid's built-in loading overlay - we use our own
        overlayLoadingTemplate: `
        <div class="custom-loading-overlay">
            <div class="custom-spinner"></div>
            <span class="loading-text">Loading data</span>
        </div>
    `,
        overlayNoRowsTemplate: `
        <div class="empty-state-container">
            <div class="empty-state-image">
                <img src="${emptySvg}" alt="No data" />
            </div>
        </div>
    `,
        groupDefaultExpanded: -1,
        animateRows: true,
        suppressAggFuncInHeader: true,
        onFilterChanged: onFilterChangedCallback,
        autoGroupColumnDef: {
            headerName: 'Teams and Users',
            minWidth: 250,
            cellRendererParams: { suppressCount: false },
            floatingFilter: true,
            filter: 'agTextColumnFilter',
            pinned: 'left',
            cellStyle: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start'
            },
            valueGetter: (params: { data?: Record<string, unknown> }) => {
                if (params?.data)
                {
                    return params.data?.userDisplayName;
                }
                return '';
            }
        }
    };
}

// Check if shift/task filters are applied
function hasShiftTaskFilters(facets?: FacetFilters): boolean {
    return !!(
        facets?.shiftStartFacet?.boundaries ||
        facets?.tasks?.value ||
        facets?.leaveTasks?.value ||
        facets?.shiftLocations?.value ||
        facets?.tasksStartFacet?.boundaries ||
        facets?.shiftTemplates?.value
    );
}

export function mergeBackendResponse(
    userIdsString: string,
    backendResp: ShiftResponseItem[],
    facets?: FacetFilters
): ShiftResponseItem[] | [] {
    if (!userIdsString)
    {
        return [];
    }
    const userIds = userIdsString.split(',').map((id: string) => Number(id.trim()));

    const respMap = new Map(backendResp.map((item: ShiftResponseItem) => [ Number(item._id), item ]));

    // If shift/task filters are applied, exclude users without shifts
    if (hasShiftTaskFilters(facets)) {
        return userIds
            .map((id: number) => respMap.get(id))
            .filter((item): item is ShiftResponseItem => item !== undefined);
    }

    // Otherwise, show all users (with empty shifts if needed)
    return userIds.map((id: number) => {
        return respMap.get(id) || {
            _id: Number(id),
            user: {
                id: Number(id),
                email: '',
                name: '',
                timezone: ''
            },
            shifts: []
        };
    });
}
